/* eslint-disable */
import { Module } from 'vuex';
import api from '@/services/api';
import { SHOP_ID_STORAGE_KEY, SHOP_INFO_STORAGE_KEY } from '@/config/app';

export interface ShopInfo {
  id: number | null;
  name: string | null;
  legal_name: string | null;
  phone: string | null;
  whatsapp_phone?: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  tax_id: string | null;
  trade_register: string | null;
  currency_code: string | null;
  currency_position?: string | null;
  decimal_places?: number | null;
  decimal_separator?: string | null;
  thousands_separator?: string | null;
  logo_url: string | null;
  main_logo_url?: string | null;
  footer_note: string | null;
  timezone: string | null;
  tva_rate: number | null;
  css_rate: number | null;
  code: string | null;
  is_active: boolean;
  is_main?: boolean | null;
  is_complete?: boolean;
  cash_count_enabled?: boolean | null;
  cash_register_auto_close_enabled?: boolean | null;
  ecommerce_primary_color?: string | null;
  ecommerce_accent_color?: string | null;
  ecommerce_hero_title?: string | null;
  ecommerce_hero_cover_url?: string | null;
  ecommerce_whatsapp_enabled?: boolean | null;
  ecommerce_enabled?: boolean | null;
}

export interface ShopState {
  currentShopId: number | null;     // site courant (id)
  info: ShopInfo | null;            // détails du site courant
  isLoading: boolean;
  isLoadedOnce: boolean;
  lastFetchedAt: number | null;
}

export interface RootState {
  // laisse vide volontairement (évite les erreurs TS si ton RootState n'est pas strict)
}

const LS_SHOP_ID_KEY = SHOP_ID_STORAGE_KEY;
const LS_SHOP_INFO_KEY = SHOP_INFO_STORAGE_KEY;
const SHOP_INFO_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

const state: ShopState = {
  currentShopId: null,
  info: null,
  isLoading: false,
  isLoadedOnce: false,
  lastFetchedAt: null,
};

const shopModule: Module<ShopState, RootState> = {
  namespaced: true,
  state,

  getters: {
    currentShopId(s): number | null {
      return s.currentShopId;
    },
    shopInfo(s): ShopInfo | null {
      return s.info;
    },
    shopName(s): string {
      return s.info?.name || '';
    },
    timezone(s): string | null {
      return s.info?.timezone || null;
    },
    tvaRate(s): number | null {
      return s.info?.tva_rate != null ? Number(s.info.tva_rate) : null;
    },
    cssRate(s): number | null {
      return s.info?.css_rate != null ? Number(s.info.css_rate) : null;
    },
    isComplete(s): boolean {
      return !!(s.info && s.info.is_complete);
    },
    isLoadedOnce(s): boolean {
      return s.isLoadedOnce;
    },
    hasCurrentShopInfo(s): boolean {
      return !!(
        s.currentShopId &&
        s.info?.id &&
        Number(s.info.id) === Number(s.currentShopId)
      );
    },
    isInfoRefreshDue(s): boolean {
      return !!(
        s.currentShopId &&
        s.info?.id &&
        s.lastFetchedAt &&
        Date.now() - s.lastFetchedAt >= SHOP_INFO_REFRESH_INTERVAL_MS
      );
    },
    hasCurrentShop(s): boolean {
      return !!s.currentShopId;
    },
    isLoading(s): boolean {
      return s.isLoading;
    },
  },

  mutations: {
    SET_LOADING(s, loading: boolean) {
      s.isLoading = loading;
    },

    SET_CURRENT_SHOP_ID(s, id: number | null) {
      s.currentShopId = id;
      if (id) {
        window.localStorage.setItem(LS_SHOP_ID_KEY, String(id));
      } else {
        window.localStorage.removeItem(LS_SHOP_ID_KEY);
      }
    },

    SET_INFO(s, payload: ShopInfo | null) {
      s.info = payload;
      s.isLoadedOnce = true;
      s.lastFetchedAt = payload ? Date.now() : null;

      if (payload) {
        window.localStorage.setItem(LS_SHOP_INFO_KEY, JSON.stringify(payload));
      } else {
        window.localStorage.removeItem(LS_SHOP_INFO_KEY);
      }
    },

    CLEAR_SHOP_CONTEXT(s) {
      s.currentShopId = null;
      s.info = null;
      s.isLoadedOnce = false;
      s.lastFetchedAt = null;
      window.localStorage.removeItem(LS_SHOP_ID_KEY);
      window.localStorage.removeItem(LS_SHOP_INFO_KEY);
    },
  },

  actions: {
    /**
     * Rehydrate (au reload) : remet shopId + info depuis localStorage dans Vuex.
     * Ne fait aucun appel réseau.
     */
    LOAD_FROM_LOCALSTORAGE({ commit }) {
      const rawId = window.localStorage.getItem(LS_SHOP_ID_KEY);
      const rawInfo = window.localStorage.getItem(LS_SHOP_INFO_KEY);

      if (rawId) {
        const id = Number(rawId);
        if (!Number.isNaN(id)) {
          commit('SET_CURRENT_SHOP_ID', id);
        }
      }

      if (rawInfo) {
        try {
          const info = JSON.parse(rawInfo);
          commit('SET_INFO', info);
        } catch (e) {
          // si invalide, on nettoie
          commit('SET_INFO', null);
        }
      }
    },

    /**
     * Au login : si user.shop_id existe => on force ce shop comme shop courant
     * et on charge ses infos.
     */
    async INIT_FROM_AUTH_USER({ commit, dispatch }, user: any) {
      // si user a un shop_id, c'est le shop imposé (caissier, gérant affecté, etc.)
      if (user && user.shop_id) {
        commit('SET_CURRENT_SHOP_ID', Number(user.shop_id));
        await dispatch('fetchInfo');
        return;
      }

      // sinon (gérant sans shop_id) on ne choisit rien ici :
      // il ira sur SitesView pour choisir
      commit('SET_CURRENT_SHOP_ID', null);
      commit('SET_INFO', null);
    },

    /**
     * Charge les infos du site courant (X-Shop-Id est envoyé par axios interceptor).
     * Ne fait rien si pas de shopId.
     */
    async fetchInfo(
      { commit, state, rootGetters },
      options: { silent?: boolean; throwOnError?: boolean } | null = {}
    ) {
      const currentUser = rootGetters['auth/currentUser'];
      if (!currentUser) return;
      if (!state.currentShopId) return;

      if (!options?.silent) {
        commit('SET_LOADING', true);
      }

      try {
        const requestConfig = options?.silent ? ({ skipErrorToast: true } as any) : undefined;
        const res = await api.get('/shop-info', requestConfig);
        const data: ShopInfo = (res.data && res.data.data) || res.data;
        commit('SET_INFO', data);
      } catch (error: any) {
        if (error?.response?.status === 401 && !options?.throwOnError) return;
        console.error('Erreur chargement infos boutique', error);
        if (options?.throwOnError) throw error;
      } finally {
        if (!options?.silent) {
          commit('SET_LOADING', false);
        }
      }
    },

    /**
     * Change de site (gérant) :
     * - stocke shopId dans Vuex + localStorage
     * - charge shop-info
     */
    async changeSite(
      { commit, dispatch, rootGetters, state },
      payload: number | { shopId: number; silent?: boolean }
    ) {
      const currentUser = rootGetters['auth/currentUser'];
      if (!currentUser) return;

      const shopId = typeof payload === 'number' ? payload : payload.shopId;
      const silent = typeof payload === 'number' ? false : Boolean(payload.silent);
      const isGlobalUser = currentUser.role === 'utilisateur' && !currentUser.shop_id;
      const canSwitchShops = Array.isArray(currentUser.permissions)
        && currentUser.permissions.includes('shops.switch');
      const accessibleShopIds = Array.isArray(currentUser.accessible_shops)
        ? currentUser.accessible_shops.map((shop: { id: number }) => Number(shop.id))
        : [];

      if (isGlobalUser && !canSwitchShops) {
        throw new Error('Vous n’avez pas la permission de changer de site.');
      }

      if (isGlobalUser && !accessibleShopIds.includes(Number(shopId))) {
        throw new Error('Ce site ne fait pas partie des boutiques accessibles.');
      }

      const previousShopId = state.currentShopId;
      const previousShopInfo = state.info;

      commit('SET_CURRENT_SHOP_ID', shopId);
      try {
        await dispatch('fetchInfo', { silent, throwOnError: true });
        await dispatch('cashRegister/clear', null, { root: true });
      } catch (error) {
        commit('SET_CURRENT_SHOP_ID', previousShopId);
        commit('SET_INFO', previousShopInfo);
        throw error;
      }
    },

    async clearShopContext({ commit, dispatch }) {
      commit('CLEAR_SHOP_CONTEXT');
      await dispatch('cashRegister/clear', null, { root: true });
    },

    async saveInfo({ commit, state, rootGetters }, payload: Partial<ShopInfo>) {
      const currentUser = rootGetters['auth/currentUser'];
      if (!currentUser) return;

      if (!state.currentShopId) {
        throw new Error('Aucun site sélectionné.');
      }

      const res = await api.post('/shop-info', payload);
      const data: ShopInfo = (res.data && res.data.data) || res.data;
      commit('SET_INFO', data);
      return res;
    },
  },
};

export default shopModule;
